import clsx from 'clsx';
import type { ReactNode } from 'react';

import * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import { type ButtonProps, IconStack, renderIcon } from '@gitbook/spine-react';

export const DEFAULT_CHANGE_REQUEST_STATUSES = [
    api.ChangeRequestStatus.Draft,
    api.ChangeRequestStatus.Open,
] as const;

type ChangeRequestStatusDescriptor = {
    label: string;
    description: string;
    /** A pre-rendered icon element (via `renderIcon`) so each status can carry its own styling. */
    icon: ReactNode;
    textColorClassName: string;
};

const CHANGE_REQUEST_STATUSES_MAP: Record<api.ChangeRequestStatus, ChangeRequestStatusDescriptor> =
    {
        [api.ChangeRequestStatus.Draft]: {
            label: 'Draft',
            description: 'This change request is currently a draft',
            icon: renderIcon(Icon.ChangeRequestDraft, { className: 'text-[var(--neutral-12)]' }),
            textColorClassName: 'text-[var(--neutral-12)]',
        },
        [api.ChangeRequestStatus.Open]: {
            label: 'In review',
            description: 'This change request has been submitted for review',
            icon: renderIcon(Icon.ChangeRequestOpen, { className: 'text-[var(--green-status)]' }),
            textColorClassName: 'text-[var(--green-status)]',
        },
        [api.ChangeRequestStatus.Merged]: {
            label: 'Merged',
            description: 'This change request has been merged into the space',
            icon: renderIcon(Icon.ChangeRequestMerged, { className: 'text-[var(--merge-10)]' }),
            textColorClassName: 'text-[var(--merge-10)]',
        },
        [api.ChangeRequestStatus.Archived]: {
            label: 'Archived',
            description: 'This change request has been archived',
            icon: renderIcon(Icon.ChangeRequestArchive, { className: 'text-[var(--neutral-11)]' }),
            textColorClassName: 'text-[var(--neutral-11)]',
        },
    };

export const CHANGE_REQUEST_STATUSES = Object.entries(CHANGE_REQUEST_STATUSES_MAP).map(
    ([status, entry]) => ({
        ...entry,
        value: status as api.ChangeRequestStatus,
    })
);

/**
 * Returns the selected statuses for change request list filters.
 */
export function getChangeRequestFilterStatuses(
    statuses: api.ChangeRequestStatus[] | null | undefined
): [api.ChangeRequestStatus, ...api.ChangeRequestStatus[]] {
    const [firstStatus, ...otherStatuses] = statuses ?? [];
    return firstStatus ? [firstStatus, ...otherStatuses] : [...DEFAULT_CHANGE_REQUEST_STATUSES];
}

/** Whether statuses are equivalent to the default change request status filter. */
export function isDefaultChangeRequestStatusFilter(
    statuses: readonly api.ChangeRequestStatus[]
): boolean {
    return (
        statuses.length === DEFAULT_CHANGE_REQUEST_STATUSES.length &&
        DEFAULT_CHANGE_REQUEST_STATUSES.every((status) => statuses.includes(status))
    );
}

/**
 * Returns the button props for selected change request status filters.
 */
export function getChangeRequestFilterButtonProps(
    statuses: api.ChangeRequestStatus[] | null | undefined
): Pick<ButtonProps, 'label' | 'leadingIcon'> {
    const selectedStatuses = getChangeRequestFilterStatuses(statuses);

    if (selectedStatuses.length === CHANGE_REQUEST_STATUSES.length) {
        return {
            label: 'All statuses',
            leadingIcon: Icon.All,
        };
    }

    if (selectedStatuses.length === 1) {
        const [selectedStatus] = selectedStatuses;
        return {
            label: `Status: ${getChangeRequestStatusLabel(selectedStatus)}`,
            leadingIcon: <ChangeRequestStatusIcon status={selectedStatus} colored />,
        };
    }

    return {
        label: `${selectedStatuses.length} statuses`,
        leadingIcon: <ChangeRequestStatusIconStack statuses={selectedStatuses} />,
    };
}

/**
 * Returns the descriptor of a change request status.
 */
export function getChangeChangeRequestStatusDescriptor(
    status: api.ChangeRequestStatus
): ChangeRequestStatusDescriptor {
    return CHANGE_REQUEST_STATUSES_MAP[status];
}

/**
 * Returns the description of a change request status.
 */
export function getChangeRequestStatusDescription(status: api.ChangeRequestStatus): string {
    return getChangeChangeRequestStatusDescriptor(status).description;
}

/**
 * Returns the label of a change request status.
 */
export function getChangeRequestStatusLabel(status: api.ChangeRequestStatus) {
    return getChangeChangeRequestStatusDescriptor(status).label;
}

/**
 * Returns the icon of a change request status.
 */
export function getChangeRequestStatusIcon(status: api.ChangeRequestStatus) {
    return getChangeChangeRequestStatusDescriptor(status).icon;
}

/**
 * Icon of a change request status.
 */
export function ChangeRequestStatusIcon(props: {
    status: api.ChangeRequestStatus;
    className?: string;
    size?: number;
    /**
     * Color the icon.
     * @default false
     */
    colored?: boolean;
}) {
    const { status, className, size, colored } = props;
    const descriptor = getChangeChangeRequestStatusDescriptor(status);
    return renderIcon(getChangeRequestStatusIcon(status), {
        size,
        className: clsx(colored ? descriptor.textColorClassName : null, className),
    });
}

function ChangeRequestStatusIconStack(props: { statuses: api.ChangeRequestStatus[] }) {
    const { statuses } = props;

    return (
        <IconStack
            max={statuses.length}
            size="size-4"
            className="border-neutral-subtle! bg-base! [&:not(:last-child)]:-mr-1.5!"
            items={statuses.map((status) => ({
                key: status,
                icon: <ChangeRequestStatusIcon status={status} size={10} colored />,
            }))}
        />
    );
}
